import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faEye, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import Circles from '../../../assets/images/circle.png';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const DistrictLoginDashboard = ({ 
  loading, 
  handleMoreInfoClick 
}) => {
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  
  // Check if current user is CRM component
  const userTypeCode = sessionStorage.getItem("userTypeCodeModel");
  const isCRMComponent = parseInt(userTypeCode) === 2 && sessionStorage.getItem("stateNameModel") === "3";
  
  // Local state for District Admin specific data
  const [surveyorCreationCount, setSurveyorCreationCount] = useState(0);
  const [surveyorCreationDetails, setSurveyorCreationDetails] = useState([]);
  const [surveyorCreationVisible, setSurveyorCreationVisible] = useState(false);
  const [apiDataReportCount, setApiDataReportCount] = useState(0);
  const [apiDataReportDetails, setApiDataReportDetails] = useState([]);
  const [selectedFinYear, setSelectedFinYear] = useState("");
  const [expandedApiDataReportIndex, setExpandedApiDataReportIndex] = useState(null);
  const [expandedApiDataReportDistrictData, setExpandedApiDataReportDistrictData] = useState([]);
  const [expandedApiDataReportSubDistrictIndex, setExpandedApiDataReportSubDistrictIndex] = useState(null);
  const [expandedApiDataReportSubDistrictDetailsData, setExpandedApiDataReportSubDistrictDetailsData] = useState([]);
  const [apiDataReportVisible, setApiDataReportVisible] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showImplementsModal, setShowImplementsModal] = useState(false);
  const [selectedImplements, setSelectedImplements] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [selectedDistrictDataType, setSelectedDistrictDataType] = useState(
    () => sessionStorage.getItem('districtDashboardDataType') || "CHC"
  );
  
  // Drill-down state
  const [expandedRows, setExpandedRows] = useState({}); // Track which district rows are expanded
  const [expandedSurveyorRows, setExpandedSurveyorRows] = useState({}); // Track which surveyor rows are expanded
  const [surveyorDetailsByDistrict, setSurveyorDetailsByDistrict] = useState({}); // Store surveyor details by district
  const [farmerDetailsBySurveyor, setFarmerDetailsBySurveyor] = useState({}); // Store farmer details by surveyor
  const [chcDetailsBySurveyor, setChcDetailsBySurveyor] = useState({}); // Store CHC details by surveyor
  const [expBenBySurveyor, setExpBenBySurveyor] = useState({}); // Expensive machinery beneficiaries by surveyor
  const [expImagesByBeneficiary, setExpImagesByBeneficiary] = useState({});
  const [expImplementsByBeneficiary, setExpImplementsByBeneficiary] = useState({});
  const [loadingFarmers, setLoadingFarmers] = useState({}); // Track which surveyors are loading farmer data

  // Helper function to get district code from session storage
  const getDistrictCode = (key) => {
    const districtNameFromSession = sessionStorage.getItem(key);
    console.log('District name from session:', districtNameFromSession);

    if (!districtNameFromSession) {
      console.warn('District name not found in session storage');
      return null;
    }

    // Handle multiple district codes (comma-separated)
    if (districtNameFromSession.includes(',')) {
      const districtCodes = districtNameFromSession.split(',').map(code => code.trim());
      console.log('Multiple district codes:', districtCodes);
      return districtCodes.join(',');
    }

    // Single district code
    const districtCodeFromSession = districtNameFromSession;
    console.log('Single district code:', districtCodeFromSession);
    return districtCodeFromSession;
  };

  // Map radio selection to API "type" value
  const getDistrictDataTypeCode = (dataType) => {
    switch (dataType) {
      case "CHC":
        return 1;
      case "Demonstration":
        return 2;
      case "Individual":
        return 3;
      default:
        return 1;
    }
  };

  const handleDistrictDataTypeChange = async (dataType) => {
    sessionStorage.setItem('districtDashboardDataType', dataType);
    setSelectedDistrictDataType(dataType);
    setExpandedRows({});
    setExpandedSurveyorRows({});
    setSurveyorDetailsByDistrict({});
    setFarmerDetailsBySurveyor({});
    setChcDetailsBySurveyor({});
    setExpBenBySurveyor({});
    await fetchDistrictDetailsForTable(dataType);
    await fetchSurveyorCreationData(dataType);
  };

  // District Admin API calls - Fetch Surveyor Count
  const fetchSurveyorCreationData = async (dataType = selectedDistrictDataType) => {
    try {
      const createdBy = sessionStorage.getItem("userCodeModel");
      if (!createdBy) {
        console.error('User code not found in session storage');
        return;
      }

      // Use the helper function to get district codes (single or multiple)
      const districtCode = getDistrictCode("districtNameModel");
      const stateCode = parseInt(sessionStorage.getItem("stateNameModel"));
      
      if (!districtCode || !stateCode) {
        console.error('District code or state code not found in session storage');
        setSurveyorCreationCount(0);
        return;
      }
      
      console.log('🎯 District Dashboard - Using district code:', districtCode);
      console.log('🎯 District Dashboard - Using state code:', stateCode);
      
      // Get userTypeCode from session storage and ensure it's a number
      const userTypeCode = parseInt(sessionStorage.getItem("userTypeCodeModel")) || 2;
      console.log('🎯 District Dashboard - Using userTypeCode:', userTypeCode);
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/GetDistrictDashboarCount`,
        {
           "flag": "District",
           "userTypeCode": userTypeCode,
           "stateCode": stateCode, 
          "districtCode": sessionStorage.getItem("districtCodeModel") || String(districtCode),
          "type": getDistrictDataTypeCode(dataType)
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('🎯 District Dashboard Count API Response (District):', data);

      if (data.success && data.dataContent && data.dataContent.length > 0) {
        // Calculate total users from all districts in dataContent (for count display)
        const totalUsers = data.dataContent.reduce((sum, district) => {
          return sum + parseInt(district.totalUsers || 0);
        }, 0);
        setSurveyorCreationCount(totalUsers);
        // Don't set details here - that's for the table
        console.log('✅ District Total Count:', totalUsers);
        console.log('✅ District Count Data:', data.dataContent);
      } else {
        setSurveyorCreationCount(0);
        console.log('❌ No District Count data received');
      }
    } catch (error) {
      console.error('❌ Error fetching District Surveyor data:', error);
      setSurveyorCreationCount(0);
    }
  };

  const fetchDistrictDetailsForTable = async (dataType = selectedDistrictDataType) => {
  try {
    const districtCode = getDistrictCode("districtNameModel");
    const stateCode = parseInt(sessionStorage.getItem("stateNameModel"));

    if (!districtCode || !stateCode) {
      console.error('District code or state code not found in session storage');
      return;
    }

    const userTypeCode = parseInt(sessionStorage.getItem("userTypeCodeModel")) || 2;

    const { data } = await axios.post(
      `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/GetDistrictDashboarCount`,
      {
        "flag": "District",
        "userTypeCode": userTypeCode,
        "stateCode": stateCode,
        "districtCode": sessionStorage.getItem("districtCodeModel"),
        "type": getDistrictDataTypeCode(dataType)
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    console.log('🎯 District Details Table API Response:', data);

    if (data.success && data.dataContent && data.dataContent.length > 0) {
      setSurveyorCreationDetails(data.dataContent || []);
    } else {
      setSurveyorCreationDetails([]);
    }
  } catch (error) {
    console.error('❌ Error fetching District Details for table:', error);
    setSurveyorCreationDetails([]);
  }
};

  // Fetch API Data Report count
  const fetchAPIDataReport = async () => {
    try {
      const districtCodeStr = getDistrictCode("districtNameModel");
      const stateCode = parseInt(sessionStorage.getItem("stateNameModel"));
      
      if (!districtCodeStr || !stateCode) {
        console.error('District code or state code not found in session storage');
        setApiDataReportCount(0);
        return;
      }
      
      // Handle comma-separated district codes - use the first one
      const districtCode = districtCodeStr.includes(',') 
        ? parseInt(districtCodeStr.split(',')[0].trim()) 
        : parseInt(districtCodeStr);
      
      console.log('fetchAPIDataReport - District Admin API call');
      console.log('Using districtCode:', districtCode);
      console.log('Using stateCode:', stateCode);
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/APIDataReport`,
        {
          "finYear": "",
          "stateCode": stateCode,
          "districtCode": sessionStorage.getItem("districtCodeModel"),
          "subdistrictCode": 0,
          "userTypeCode": 0
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('API Data Report Response:', data);
      
      let recordCount = 0;
      if (data && data.success) {
        // API returns recordCount at the top level
        if (data.recordCount !== undefined && data.recordCount !== null) {
          recordCount = parseInt(data.recordCount) || 0;
        } else if (data.dataContent && Array.isArray(data.dataContent)) {
          // Fallback: use dataContent length if recordCount is not available
          recordCount = data.dataContent.length;
        }
      }
      
      console.log('Extracted recordCount:', recordCount);
      setApiDataReportCount(recordCount);
    } catch (error) {
      console.error('Error fetching API Data Report count:', error);
      setApiDataReportCount(0);
    }
  };

  // Toggle row expansion and fetch surveyor details
  const toggleRowExpansion = async (districtData, districtIndex) => {
    const districtKey = districtData.districtCode || districtIndex;
     // Make cache key type-aware so switching CHC/Demonstration/Individual re-fetches correctly
  const cacheKey = `${districtKey}-${selectedDistrictDataType}`;
    // If already expanded, just collapse
    if (expandedRows[districtKey]) {
      setExpandedRows(prev => {
        const newState = { ...prev };
        delete newState[districtKey];
        return newState;
      });
      return;
    }

    // If surveyor details already exist, just expand
    if (Object.prototype.hasOwnProperty.call(surveyorDetailsByDistrict, cacheKey)) {
      setExpandedRows({ [districtKey]: true });
      setExpandedSurveyorRows({});
      return;
    }

    // Fetch surveyor details
    try {
      const userTypeCode = parseInt(sessionStorage.getItem("userTypeCodeModel")) || 2;
      const stateCode = parseInt(sessionStorage.getItem("stateNameModel"));
      const districtCode = districtData.districtCode;
      
      console.log('🎯 Surveyor Details - Using district code:', districtCode);
      console.log('🎯 Surveyor Details - Selected district:', districtData);
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/GetDistrictDashboarCount`,
        {
            "flag": "Surveyor",
            "userTypeCode": userTypeCode,
            "stateCode": stateCode, 
            "districtCode": districtData.districtCode || sessionStorage.getItem("districtCodeModel"),
           "type": getDistrictDataTypeCode(selectedDistrictDataType)
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('🎯 Surveyor Details API Response:', data);

      if (data.success && data.dataContent && data.dataContent.length > 0) {
        // Store surveyor details for this district
        setSurveyorDetailsByDistrict(prev => ({
          ...prev,
          [cacheKey]: data.dataContent || []
        }));
        
        // Expand the row (accordion: close other districts)
        setExpandedRows({ [districtKey]: true });
        setExpandedSurveyorRows({});
        
        console.log('✅ Surveyor Details loaded for district:', districtData.districtName);
        console.log('✅ Surveyor data:', data.dataContent);
      } else {
        setSurveyorDetailsByDistrict(prev => ({
          ...prev,
          [cacheKey]: []
        }));
        setExpandedRows({ [districtKey]: true });
        setExpandedSurveyorRows({});
        console.log('❌ No Surveyor Details data received for district:', districtData.districtName);
      }
    } catch (error) {
      console.error('❌ Error fetching Surveyor Details:', error);
    }
  };

  // Toggle surveyor row expansion and fetch farmer/CHC/Individual details
  const toggleSurveyorExpansion = async (surveyorData, surveyorKey) => {
    if (expandedSurveyorRows[surveyorKey]) {
      setExpandedSurveyorRows(prev => {
        const newState = { ...prev };
        delete newState[surveyorKey];
        return newState;
      });
      return;
    }

    if (selectedDistrictDataType === "Individual") {
      if (Object.prototype.hasOwnProperty.call(expBenBySurveyor, surveyorKey)) {
        setExpandedSurveyorRows(prev => ({ ...prev, [surveyorKey]: true }));
        return;
      }

      setLoadingFarmers(prev => ({ ...prev, [surveyorKey]: true }));
      try {
        const { data } = await axios.post(
          `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/ExpensiveMachinery/GetExpBenData`,
          { createdBy: surveyorData.userCode || surveyorData.id },
          { headers: { accept: '*/*', 'Content-Type': 'application/json' } }
        );

        const beneficiaries = data?.success && Array.isArray(data.dataContent) ? data.dataContent : [];
        setExpBenBySurveyor(prev => ({ ...prev, [surveyorKey]: beneficiaries }));
        setExpandedSurveyorRows(prev => ({ ...prev, [surveyorKey]: true }));

        if (beneficiaries.length === 0) {
          console.log('No beneficiary data for surveyor:', surveyorData.userName);
        }
      } catch (err) {
        console.error('Error fetching ExpensiveMachinery beneficiaries:', err);
        setExpBenBySurveyor(prev => ({ ...prev, [surveyorKey]: [] }));
        setExpandedSurveyorRows(prev => ({ ...prev, [surveyorKey]: true }));
        alert('Error fetching beneficiary data.');
      } finally {
        setLoadingFarmers(prev => {
          const newState = { ...prev };
          delete newState[surveyorKey];
          return newState;
        });
      }
      return;
    }

    if (selectedDistrictDataType === "CHC" && Object.prototype.hasOwnProperty.call(chcDetailsBySurveyor, surveyorKey)) {
      setExpandedSurveyorRows(prev => ({ ...prev, [surveyorKey]: true }));
      return;
    }

    if (selectedDistrictDataType === "Demonstration" && Object.prototype.hasOwnProperty.call(farmerDetailsBySurveyor, surveyorKey)) {
      setExpandedSurveyorRows(prev => ({ ...prev, [surveyorKey]: true }));
      return;
    }

    setLoadingFarmers(prev => ({ ...prev, [surveyorKey]: true }));

    try {
      const createdBy = surveyorData.userCode || surveyorData.id;

      if (selectedDistrictDataType === "CHC") {
        // Fetch CHC details
        console.log('🎯 CHC Details - Using surveyorUserCode:', createdBy);
        console.log('🎯 CHC Details - Selected surveyor:', surveyorData);
        
        const { data } = await axios.post(
          `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/Get_CHCDetailByID`,
          {
            surveyorUserCode: parseInt(createdBy)
          },
          {
            headers: {
              'accept': '*/*',
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('🎯 CHC Details API Response:', data);

        if (data.success && data.dataContent && data.dataContent.length > 0) {
          // Store CHC details for this surveyor
          setChcDetailsBySurveyor(prev => ({
            ...prev,
            [surveyorKey]: data.dataContent
          }));
          
          // Expand the surveyor row
          setExpandedSurveyorRows(prev => ({
            ...prev,
            [surveyorKey]: true
          }));
          
          console.log('✅ CHC Details loaded for surveyor:', surveyorData.userName);
          console.log('✅ CHC data:', data.dataContent);
          console.log(`✅ Found ${data.dataContent.length} CHC records`);
        } else {
          setChcDetailsBySurveyor(prev => ({ ...prev, [surveyorKey]: [] }));
          setExpandedSurveyorRows(prev => ({ ...prev, [surveyorKey]: true }));
          console.log('No CHC Details data received for surveyor:', surveyorData.userName);
        }
      } else if (selectedDistrictDataType === "Demonstration") {
        // Fetch farmer details (Demonstration)
       // console.log('🎯 Farmer Details - Using createdBy:', createdBy);
        console.log('🎯 Farmer Details - Selected surveyor:', surveyorData);
        
        const { data } = await axios.post(
          `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/Farmers/GetMTFarmerRegData`,
          {
            createdBy: parseInt(createdBy)
          },
          {
            headers: {
              'accept': '*/*',
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('🎯 Farmer Details API Response:', data);

        if (data.success && data.dataContent && data.dataContent.length > 0) {
          // Validate farmer data before storing
          const validFarmerData = data.dataContent.filter(farmer => 
            farmer && farmer.farmerCode && farmer.farmerName
          );
          
          if (validFarmerData.length === 0) {
            setFarmerDetailsBySurveyor(prev => ({ ...prev, [surveyorKey]: [] }));
            setExpandedSurveyorRows(prev => ({ ...prev, [surveyorKey]: true }));
            console.log('No valid farmer data found for surveyor:', surveyorData.userName);
            return;
          }
          
          // Store farmer details for this surveyor
          setFarmerDetailsBySurveyor(prev => ({
            ...prev,
            [surveyorKey]: validFarmerData
          }));
          
          // Expand the surveyor row
          setExpandedSurveyorRows(prev => ({
            ...prev,
            [surveyorKey]: true
          }));
          
          console.log('✅ Farmer Details loaded for surveyor:', surveyorData.userName);
          console.log('✅ Valid farmer data:', validFarmerData);
          console.log(`✅ Found ${validFarmerData.length} valid farmers out of ${data.dataContent.length} total`);
        } else {
          setFarmerDetailsBySurveyor(prev => ({ ...prev, [surveyorKey]: [] }));
          setExpandedSurveyorRows(prev => ({ ...prev, [surveyorKey]: true }));
          console.log('No Farmer Details data received for surveyor:', surveyorData.userName);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching Details:', error);
      
      // Show user-friendly error message
      if (error.response) {
        // Server responded with error status
        console.error('❌ Server Error:', error.response.status, error.response.data);
        alert(`Server error: ${error.response.status}. Please try again later.`);
      } else if (error.request) {
        // Request was made but no response received
        console.error('❌ Network Error:', error.request);
        alert('Network error. Please check your connection and try again.');
      } else {
        // Something else happened
        console.error('❌ Unexpected Error:', error.message);
        alert('An unexpected error occurred. Please try again.');
      }
    } finally {
      // Clear loading state
      setLoadingFarmers(prev => {
        const newState = { ...prev };
        delete newState[surveyorKey];
        return newState;
      });
    }
  };

  // Fetch API Data Report Details
  const fetchAPIDataReportDetails = async (finYear = "") => {
    try {
      const districtCodeStr = getDistrictCode("districtNameModel");
      const stateCode = parseInt(sessionStorage.getItem("stateNameModel"));
      
      if (!districtCodeStr || !stateCode) {
        console.error('District code or state code not found in session storage');
        setApiDataReportDetails([]);
        return;
      }
      
      // Handle comma-separated district codes - use the first one
      const districtCode = districtCodeStr.includes(',') 
        ? parseInt(districtCodeStr.split(',')[0].trim()) 
        : parseInt(districtCodeStr);
      
      console.log('fetchAPIDataReportDetails - District Admin API call');
      console.log('Using districtCode:', districtCode);
      console.log('Using stateCode:', stateCode);
      console.log('Using finYear:', finYear);
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/APIDataReport`,
        {
          "finYear": finYear || "",
          "stateCode": stateCode,
          "districtCode": parseInt(sessionStorage.getItem("districtCodeModel")) || districtCode || 0,
          "subdistrictCode": 0,
          "userTypeCode": 0
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('API Data Report Details Response:', data);

      if (data && data.success && data.dataContent && Array.isArray(data.dataContent)) {
        // Group data by sub-district and count records per sub-district
        const subDistrictGroupMap = {};
        
        data.dataContent.forEach((item) => {
          const subDistrictName = item.subdistrictName || item.blockName || 'N/A';
          const subDistrictCode = item.subdistrictCode || item.blockCode || 0;
          
          if (!subDistrictGroupMap[subDistrictName]) {
            subDistrictGroupMap[subDistrictName] = {
              blockName: subDistrictName,
              blockCode: subDistrictCode,
              recordCount: 0
            };
          }
          subDistrictGroupMap[subDistrictName].recordCount += 1;
        });
        
        // Convert map to array
        const transformedData = Object.values(subDistrictGroupMap);
        
        console.log('Transformed API Data Report Details (grouped by sub-district):', transformedData);
        setApiDataReportDetails(transformedData);
      } else {
        console.log('No data content found in response');
        setApiDataReportDetails([]);
      }
    } catch (error) {
      console.error('Error fetching API Data Report details:', error);
      setApiDataReportDetails([]);
    }
  };

  // Handle API Data Report sub-district toggle
  const handleApiDataReportToggle = async (index, subDistrictItem) => {
    if (expandedApiDataReportIndex === index) {
      setExpandedApiDataReportIndex(null);
      setExpandedApiDataReportDistrictData([]);
      setExpandedApiDataReportSubDistrictIndex(null);
      setExpandedApiDataReportSubDistrictDetailsData([]);
      return;
    }

    setExpandedApiDataReportIndex(index);
    setExpandedApiDataReportSubDistrictIndex(null);
    setExpandedApiDataReportSubDistrictDetailsData([]);
    try {
      const districtCodeStr = getDistrictCode("districtNameModel");
      const stateCode = parseInt(sessionStorage.getItem("stateNameModel"));
      
      if (!districtCodeStr || !stateCode) {
        console.error('District code or state code not found in session storage');
        setExpandedApiDataReportDistrictData([]);
        return;
      }
      
      const districtCode = districtCodeStr.includes(',') 
        ? parseInt(districtCodeStr.split(',')[0].trim()) 
        : parseInt(districtCodeStr);
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/APIDataReport`,
        {
          "finYear": selectedFinYear || "",
          "stateCode": stateCode,
          "districtCode": parseInt(sessionStorage.getItem("districtCodeModel")) || districtCode || 0,
          "subdistrictCode": parseInt(subDistrictItem.blockCode || subDistrictItem.subdistrictCode) || 0,
          "userTypeCode": 0
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('API Data Report SubDistrict/Village Response:', data);

      if (data && data.success && data.dataContent && Array.isArray(data.dataContent)) {
        // Store the raw data for CHC details
        const transformedData = data.dataContent.map((item, idx) => ({
          id: item.chcId || item.id || idx + 1,
          chcName: item.chcName || 'N/A',
          villageName: item.villageName || 'N/A',
          recordCount: 1,
          ...item
        }));
        
        console.log('Transformed API Data Report SubDistrict Details:', transformedData);
        setExpandedApiDataReportDistrictData(transformedData);
      } else {
        console.log('No subdistrict/village data content found in response');
        setExpandedApiDataReportDistrictData([]);
      }
    } catch (error) {
      console.error('Error fetching API Data Report subdistrict details:', error);
      setExpandedApiDataReportDistrictData([]);
    }
  };

  // Handle API Data Report CHC details toggle
  const handleApiDataSubDistrictReportToggle = async (index, chcItem) => {
    if (expandedApiDataReportSubDistrictIndex === index) {
      setExpandedApiDataReportSubDistrictIndex(null);
      setExpandedApiDataReportSubDistrictDetailsData([]);
      return;
    }

    setExpandedApiDataReportSubDistrictIndex(index);
    // For district level, the CHC details are already in expandedApiDataReportDistrictData
    // We can use the specific item or fetch more details if needed
    setExpandedApiDataReportSubDistrictDetailsData([chcItem]);
  };

  // Handle Financial Year change
  const handleFinYearChange = async (e) => {
    const selectedYear = e.target.value;
    setSelectedFinYear(selectedYear);
    setExpandedApiDataReportIndex(null);
    setExpandedApiDataReportDistrictData([]);
    setExpandedApiDataReportSubDistrictIndex(null);
    setExpandedApiDataReportSubDistrictDetailsData([]);
    await fetchAPIDataReportDetails(selectedYear);
  };

  const buildImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (typeof imagePath !== 'string') return null;

    const trimmedPath = imagePath.trim();
    if (trimmedPath.toLowerCase().startsWith('http://') || trimmedPath.toLowerCase().startsWith('https://') || trimmedPath.startsWith('//')) {
      return trimmedPath;
    }

    const baseUrl = (process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_DEV_MODE
      : process.env.REACT_APP_PRO_MODE
    )?.replace(/\/+$/, '');

    if (!baseUrl) {
      return trimmedPath;
    }

    const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
    return `${baseUrl}/api/common/GetAttachbyURL?imageURL=${encodeURIComponent(normalizedPath)}`;
  };

  // Handle image view
  const handleImageView = (imagePath) => {
    const imageUrl = buildImageUrl(imagePath);
    if (imageUrl) {
      setSelectedImageUrl(imageUrl);
      setShowImageModal(true);
    }
  };

  // Close image modal
  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImageUrl(null);
  };

  // Handle implement details view
  const handleImplementsClick = (chc) => {
    if (chc && chc.noOfImplements > 0) {
      setSelectedImplements({
        ...chc,
        displayName: chc.chcName || chc.farmerName || `CHC ID ${chc.chcId || chc.beneficiaryId || 'N/A'}`,
        implementDetails: chc.implementDetailsJSON ? (() => {
          try {
            return JSON.parse(chc.implementDetailsJSON);
          } catch (error) {
            console.error('Error parsing implementDetailsJSON for CHC:', error, chc);
            return [];
          }
        })() : []
      });
      setShowImplementsModal(true);
    }
  };

  // Fetch expensive machinery images for a beneficiary and show modal
  const handleViewExpImages = async (beneficiaryId) => {
    try {
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/ExpensiveMachinery/GetExpImageData`,
        { beneficiaryId: parseInt(beneficiaryId) },
        { headers: { accept: '*/*', 'Content-Type': 'application/json' } }
      );

      console.log('ExpImages response:', data);
      if (data && data.success && Array.isArray(data.dataContent) && data.dataContent.length > 0) {
        setExpImagesByBeneficiary(prev => ({ ...prev, [beneficiaryId]: data.dataContent }));
        // Show first image if available
        const firstImage = data.dataContent[0];
        if (firstImage && (firstImage.imagePath || firstImage.imgPath)) {
          const imageUrl = buildImageUrl(firstImage.imagePath || firstImage.imgPath);
          if (imageUrl) {
            setSelectedImageUrl(imageUrl);
            setShowImageModal(true);
          } else {
            alert('No image available for this beneficiary.');
          }
        } else {
          alert('No image available for this beneficiary.');
        }
      } else {
        alert('No image data available for this beneficiary.');
      }
    } catch (error) {
      console.error('Error fetching expensive machinery images:', error);
      alert('Error fetching images.');
    }
  };

  // Fetch expensive machinery implements for a beneficiary and show modal
  const handleViewExpImplements = async (beneficiaryId) => {
    try {
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/ExpensiveMachinery/GetExpImplemnetData`,
        { beneficiaryId: parseInt(beneficiaryId) },
        { headers: { accept: '*/*', 'Content-Type': 'application/json' } }
      );

      console.log('ExpImplements response:', data);
      if (data && data.success && Array.isArray(data.dataContent) && data.dataContent.length > 0) {
        setExpImplementsByBeneficiary(prev => ({ ...prev, [beneficiaryId]: data.dataContent }));
        setSelectedImplements({
          beneficiaryId,
          displayName: data.implementName, //`Beneficiary ID ${beneficiaryId}`,
          implementDetails: data.dataContent,
          noOfImplements: data.dataContent.length
        });
        setShowImplementsModal(true);
      } else {
        alert('No implements data available for this beneficiary.');
      }
    } catch (error) {
      console.error('Error fetching expensive machinery implements:', error);
      alert('Error fetching implements.');
    }
  };

  // Close implement modal
  const handleCloseImplementsModal = () => {
    setShowImplementsModal(false);
    setSelectedImplements(null);
  };

  // Local handleMoreInfoClick function for district-specific functionality
  const handleLocalMoreInfoClick = async (type) => {
    if (type === 'surveyorCreation') {
      const willOpen = !surveyorCreationVisible;
      setSurveyorCreationVisible(willOpen);
      if (willOpen) {
        setExpandedRows({});
        setExpandedSurveyorRows({});
        await fetchDistrictDetailsForTable();
        setTimeout(() => {
          const tableElement = document.getElementById('district-surveyor-table');
          if (tableElement) {
            tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } else if (type === 'apiDataReport' && isCRMComponent) {
      setApiDataReportVisible(!apiDataReportVisible);
      if (!apiDataReportVisible) {
        setLocalLoading(true);
        try {
          await fetchAPIDataReportDetails(selectedFinYear);
          // Scroll to table after data is loaded
          setTimeout(() => {
            const tableElement = document.getElementById('api-data-report-table');
            if (tableElement) {
              tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        } finally {
          setLocalLoading(false);
        }
      }
    } else if (typeof handleMoreInfoClick === "function") {
      handleMoreInfoClick(type);
    }
  };

  const escapeCsvValue = (value) => `"${String(value ?? 'N/A').replace(/"/g, '""')}"`;

  const exportToCSV = (data, filename, headers, exportType = 'district') => {
    if (!data || data.length === 0) {
      alert("No data available to export.");
      return;
    }

    const csvRows = [headers.join(",")];
    data.forEach((item, index) => {
      let row = [index + 1];

      switch (exportType) {
        case 'surveyor':
          row.push(escapeCsvValue(item.userName || item.name));
          row.push(escapeCsvValue(item.districtName));
          row.push(escapeCsvValue(item.surveyCount ?? 0));
          break;
        case 'chc':
          row.push(escapeCsvValue(item.chcName));
          row.push(escapeCsvValue(item.contactPersonName));
          row.push(escapeCsvValue(item.mobileNo));
          row.push(escapeCsvValue(item.stateName));
          row.push(escapeCsvValue(item.districtName));
          row.push(escapeCsvValue(item.villageName));
          break;
        case 'farmer':
          row.push(escapeCsvValue(item.farmerName));
          row.push(escapeCsvValue(item.mobileNo));
          row.push(escapeCsvValue(item.districtName));
          row.push(escapeCsvValue(item.villageName));
          row.push(escapeCsvValue(item.cropName));
          row.push(escapeCsvValue(item.totalAreaInHA));
          break;
        case 'individual':
          row.push(escapeCsvValue(item.farmerName));
          row.push(escapeCsvValue(item.mobileNumber));
          row.push(escapeCsvValue(item.villageName));
          row.push(escapeCsvValue(item.stateName));
          break;
        case 'apiReport':
          row.push(escapeCsvValue(item.blockName));
          row.push(escapeCsvValue(item.recordCount || 0));
          break;
        case 'apiChc':
          row.push(escapeCsvValue(item.chcName));
          row.push(escapeCsvValue(item.landMarkAddress));
          row.push(escapeCsvValue(item.societyRegistrationNumber));
          row.push(escapeCsvValue(item.mobileNo));
          row.push(escapeCsvValue(item.noOfImplements ?? 0));
          break;
        default:
          row.push(escapeCsvValue(item.districtName));
          row.push(escapeCsvValue(item.totalUsers || 0));
          break;
      }

      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
   // const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    // UTF-8 BOM so Excel shows Hindi/Unicode names correctly
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportSurveyorCreationData = () => {
    exportToCSV(surveyorCreationDetails, "District_Report", ["S.No", "District", "User Count"]);
  };

  const exportSurveyorDetailsForDistrict = (districtData) => {
    const districtKey = districtData.districtCode;
    const cacheKey = `${districtKey}-${selectedDistrictDataType}`;
    const surveyorData = surveyorDetailsByDistrict[cacheKey] || [];
    exportToCSV(
      surveyorData,
      `Surveyor_Report_${districtData.districtName}`,
      ["S.No", "Surveyor Name", "District", "Survey Count"],
      'surveyor'
    );
  };

  const exportChcDetailsForSurveyor = (surveyorKey, surveyorData) => {
    const chcData = chcDetailsBySurveyor[surveyorKey] || [];
    exportToCSV(
      chcData,
      `CHC_Report_${surveyorData.userName}`,
      ["S.No", "CHC Name", "Contact Person", "Mobile No", "State", "District", "Village"],
      'chc'
    );
  };

  const exportFarmerDetailsForSurveyor = (surveyorKey, surveyorData) => {
    const farmerData = farmerDetailsBySurveyor[surveyorKey] || [];
    exportToCSV(
      farmerData,
      `Farmer_Report_${surveyorData.userName}`,
      ["S.No", "Farmer Name", "Mobile No", "District", "Village", "Crop Name", "Area (HA)"],
      'farmer'
    );
  };

  const exportIndividualDetailsForSurveyor = (surveyorKey, surveyorData) => {
    const individualData = expBenBySurveyor[surveyorKey] || [];
    exportToCSV(
      individualData,
      `Individual_Report_${surveyorData.userName}`,
      ["S.No", "Farmer Name", "Mobile", "Village", "State"],
      'individual'
    );
  };

  // Fetch data when component mounts
  useEffect(() => {
    sessionStorage.removeItem('districtSurveyorTableVisible');

    const fetchDistrictData = async () => {
      setLocalLoading(true);
      try {
        const savedDataType = sessionStorage.getItem('districtDashboardDataType') || selectedDistrictDataType;
        await fetchSurveyorCreationData(savedDataType);

        if (isCRMComponent) {
          await fetchAPIDataReport();
        }
      } finally {
        setLocalLoading(false);
      }
    };

    fetchDistrictData();
  }, []);

  // Restore drill-down only when returning from a detail view (not on normal page load)
  useEffect(() => {
    if (!state?.restoreState) return;

    const savedDataType = sessionStorage.getItem('districtDashboardDataType') || selectedDistrictDataType;

    setSurveyorCreationVisible(true);
    setExpandedRows(state.expandedRows || {});
    setExpandedSurveyorRows(state.expandedSurveyorRows || {});
    setSurveyorDetailsByDistrict(state.surveyorDetailsByDistrict || {});
    setFarmerDetailsBySurveyor(state.farmerDetailsBySurveyor || {});
    if (state.chcDetailsBySurveyor) {
      setChcDetailsBySurveyor(state.chcDetailsBySurveyor);
    }

    if (state.expBenBySurveyor) {
      setExpBenBySurveyor(state.expBenBySurveyor);
    } else if (state.farmerDetailsBySurveyor && savedDataType === 'Individual') {
      setExpBenBySurveyor(state.farmerDetailsBySurveyor);
    }

    const restoreTable = async () => {
      await fetchDistrictDetailsForTable(savedDataType);
    };
    restoreTable();

    // Clear navigation state so refresh / re-visit does not re-expand rows
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null });
  }, [state?.restoreState]);

  return (
    <>
      {/* Surveyor Creation Card */}
      <div className="col-xxl-6 col-xl-6">
        <div className="card info-card sales-card bg-gradient-danger card-img-holder">
          <div className="card-body">
            <img src={Circles} className="card-img-absolute" alt="circle-image" />
            <h5 className="card-title">Survey Count</h5>
            <div className="d-flex align-items-center">
              <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                <i><FontAwesomeIcon icon={faUsers} /> </i>
              </div>
              <div className="ps-3">
                <h6 id="totalUsers">{loading || localLoading ? 'Loading...' : surveyorCreationCount}</h6>
              </div>
            </div>
            <div className="mt-3">
              <button
                className='btn btn-link p-0 text-white'
                onClick={() => handleLocalMoreInfoClick('surveyorCreation')}
                title="Click for more information"
              >
                <i className="bi bi-arrow-right-circle-fill fs-6">More Info</i>
              </button>
            </div>
          </div>
        </div>
      </div>
      {isCRMComponent && (
        <div className="col-xxl-6 col-xl-6">
          <div className="card info-card sales-card bg-gradient-mid card-img-holder">
            <div className="card-body">
              <img src={Circles} className="card-img-absolute" alt="circle-image" />
              <h5 className="card-title">CRM CHC Data- API</h5>
              <div className="d-flex align-items-center">
                <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                  <i><FontAwesomeIcon icon={faUsers} /> </i>
                </div>
                <div className="ps-3">
                  <h6 id="recordCount">{loading || localLoading ? 'Loading...' : apiDataReportCount}</h6>
                </div>
              </div>
              <div className="mt-3">
                <button 
                  className='btn btn-link p-0 text-white' 
                  onClick={() => handleLocalMoreInfoClick('apiDataReport')}
                  title="Click for more information"
                >
                  <i className="bi bi-arrow-right-circle-fill fs-6">More Info</i>
                </button>
              </div>
            </div>
          </div>
        </div>
        
      )}

      {/* Expandable Details Table */}
      {surveyorCreationVisible && (
        <div className="col-lg-12" id="district-surveyor-table">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title1">District Surveyor Details</h5>
                <div className="d-flex align-items-center gap-3">
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="district-data-type"
                      id="chcRadio"
                      value="CHC"
                      checked={selectedDistrictDataType === "CHC"}
                      onChange={() => handleDistrictDataTypeChange("CHC")}
                    />
                    <label className="form-check-label" htmlFor="chcRadio">
                      CHC
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="district-data-type"
                      id="demoRadio"
                      value="Demonstration"
                      checked={selectedDistrictDataType === "Demonstration"}
                      onChange={() => handleDistrictDataTypeChange("Demonstration")}
                    />
                    <label className="form-check-label" htmlFor="demoRadio">
                      Demonstration
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="district-data-type"
                      id="individualRadio"
                      value="Individual"
                      checked={selectedDistrictDataType === "Individual"}
                      onChange={() => handleDistrictDataTypeChange("Individual")}
                    />
                    <label className="form-check-label" htmlFor="individualRadio">
                      Individual
                    </label>
                  </div>
                </div>
                <button className="btn btn-sm btn-success" onClick={exportSurveyorCreationData}>
                  Export CSV
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr. No.</th>
                      <th>District</th>
                      <th>User Count</th>
                      <th></th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {surveyorCreationDetails && surveyorCreationDetails.length > 0 ? (
                      surveyorCreationDetails.map((district, index) => {
                        const districtKey = district.districtCode || index;
                        const cacheKey = `${districtKey}-${selectedDistrictDataType}`;
                        const isExpanded = expandedRows[districtKey];
                        const surveyorData = surveyorDetailsByDistrict[cacheKey] || [];
                        
                        return (
                          <React.Fragment key={index}>
                            {/* Main District Row */}
                            <tr>
                              <td>{index + 1}</td>
                              <td>{district.districtName}</td>
                              <td>{district.totalUsers}</td>
                              <td></td>
                              <td>
                                <button 
                                  className="btn btn-sm btn-primary" 
                                  type="button" 
                                  onClick={() => toggleRowExpansion(district, index)}
                                  title={isExpanded ? "Collapse" : "Expand Surveyors"}
                                >
                                  <i><FontAwesomeIcon icon={isExpanded ? faMinus : faPlus} /></i>
                                </button>
                                {isExpanded && surveyorData.length > 0 && (
                                  <button 
                                    className="btn btn-sm btn-success ms-1" 
                                    type="button" 
                                    onClick={() => exportSurveyorDetailsForDistrict(district)}
                                    title="Export Surveyors"
                                  >
                                    <i className="bi bi-download"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                            
                            {/* Expanded Surveyor Rows */}
                            {isExpanded && (
                              <>
                                {/* Surveyor Header Row */}
                                <tr className="table-info">
                                  <td colSpan="5" className="fw-bold">
                                    Surveyors in {district.districtName}
                                  </td>
                                </tr>
                                <tr className="table-secondary">
                                  <th>Sr. No.</th>
                                  <th>Surveyor Name</th>
                                  <th>District</th>
                                  <th>Surveyor Survey Count</th>
                                  <th>Action</th>
                                </tr>
                                {/* Surveyor Data Rows */}
                                {surveyorData.length > 0 ? (
                                  surveyorData.map((surveyor, surveyorIndex) => {
                                    const surveyorKey = `${index}-${surveyorIndex}`;
                                    const isSurveyorExpanded = expandedSurveyorRows[surveyorKey];
                                    const farmerData = farmerDetailsBySurveyor[surveyorKey] || [];
                                    const chcData = chcDetailsBySurveyor[surveyorKey] || [];
                                    const individualData = expBenBySurveyor[surveyorKey] || [];
                                    const displayData =
                                      selectedDistrictDataType === "CHC"
                                        ? chcData
                                        : selectedDistrictDataType === "Individual"
                                          ? individualData
                                          : farmerData;
                                    const dataTypeLabel =
                                      selectedDistrictDataType === "CHC"
                                        ? "CHC"
                                        : selectedDistrictDataType === "Individual"
                                          ? "Individuals"
                                          : "Farmers";
                                    
                                    return (
                                      <React.Fragment key={surveyorKey}>
                                        <tr className="table-light">
                                          <td>{surveyorIndex + 1}</td>
                                          <td>{surveyor.userName || 'N/A'}</td>
                                          <td>{surveyor.districtName || district.districtName || 'N/A'}</td>
                                          <td>{surveyor.surveyCount}</td>
                                          <td>
                                            <button 
                                              className="btn btn-sm btn-warning me-1" 
                                              type="button" 
                                              onClick={() => toggleSurveyorExpansion(surveyor, surveyorKey)}
                                              title={isSurveyorExpanded ? `Collapse ${dataTypeLabel}` : `Expand ${dataTypeLabel}`}
                                              disabled={loadingFarmers[surveyorKey]}
                                            >
                                              {loadingFarmers[surveyorKey] ? (
                                                <i className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></i>
                                              ) : (
                                                <i><FontAwesomeIcon icon={isSurveyorExpanded ? faMinus : faPlus} /></i>
                                              )}
                                            </button>
                                            {isSurveyorExpanded && displayData.length > 0 && (
                                              <button 
                                                className="btn btn-sm btn-success me-1" 
                                                type="button" 
                                                onClick={() => {
                                                  if (selectedDistrictDataType === "CHC") {
                                                    exportChcDetailsForSurveyor(surveyorKey, surveyor);
                                                  } else if (selectedDistrictDataType === "Individual") {
                                                    exportIndividualDetailsForSurveyor(surveyorKey, surveyor);
                                                  } else {
                                                    exportFarmerDetailsForSurveyor(surveyorKey, surveyor);
                                                  }
                                                }}
                                                title={`Export ${dataTypeLabel}`}
                                              >
                                                <i className="bi bi-download"></i>
                                              </button>
                                            )}
                                           
                                          </td>
                                        </tr>
                                        
                                        {/* Expanded Data Rows (CHC or Farmers) */}
                                        {isSurveyorExpanded && (
                                          <>
                                            {selectedDistrictDataType === "CHC" ? (
                                              /* CHC Table */
                                              <tr>
                                                <td colSpan="5" className="p-0">
                                                  <div className="table-responsive">
                                                    <table className="table table-bordered mb-0">
                                                      <thead>
                                                        <tr className="table-warning">
                                                          <th colSpan="8" className="fw-bold">
                                                            CHC under {surveyor.userName}
                                                          </th>
                                                        </tr>
                                                        <tr className="table-secondary">
                                                          <th>Sr. No.</th>
                  <th>CHC Name</th>
                  <th>Contact Person</th>
                  <th>Mobile No</th>
                  <th>State</th>
                  <th>District</th>
                  <th>Village</th>
                  <th>View Details</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {chcData.length > 0 ? (
                                                          chcData.map((chc, chcIndex) => (
                                                            <tr key={`${surveyorKey}-chc-${chcIndex}`}>
                                                              <td>{chcIndex + 1}</td>
                                                                                    <td>{chc.chcName || 'N/A'}</td>
                                                                                    <td>{chc.contactPersonName || 'N/A'}</td>
                                                                                    <td>{chc.mobileNo || 'N/A'}</td>
                                                                                    <td>{chc.stateName || 'N/A'}</td>
                                                                                    <td>{chc.districtName || 'N/A'}</td>
                                                                                    <td>{chc.villageName || 'N/A'}</td>
                                                                                    <td>
                                                                                      <button 
                                                                                        className={`btn ${chc && chc.chcId ? 'btn-primary' : 'btn-secondary'}`}
                                                                                        type="button" 
                                                                                        onClick={() => {
                                                                                          console.log('CHC data:', chc);
                                                                                          
                                                                                          // Validate CHC data before navigation
                                                                                          if (!chc || !chc.chcId) {
                                                                                            alert('CHC data is not available or incomplete. Please try again.');
                                                                                            console.error('❌ Invalid CHC data:', chc);
                                                                                            return;
                                                                                          }
                                                                                          
                                                                                          const chcId = chc.chcId;
                                                                                          const surveyorUserCode = surveyor.userCode || surveyor.id;
                                                                                          console.log('✅ Using CHC ID:', chcId);
                                                                                          console.log('✅ Using Surveyor User Code:', surveyorUserCode);
                                                                                          
                                                                                          // Navigate to the CHC Detail View page with the selected CHC ID, surveyorUserCode, and farmerCode
                                                                                          navigate("/MNT/CHCDetailView", { 
                                                                                            state: { 
                                                                                              chcId,
                                                                                              surveyorUserCode: parseInt(surveyorUserCode),
                                                                                              farmerData: { farmerCode: chc.farmerCode || chc.chcId.toString() },
                                                                                              returnTo: "/MNT/dashboardMnT",
                                                                                              restoreState: true,
                                                                                              expandedRows,
                                                                                              expandedSurveyorRows,
                                                                                              surveyorDetailsByDistrict,
                                                                                              chcDetailsBySurveyor,
                                                                                              farmerDetailsBySurveyor
                                                                                            } 
                                                                                          });
                                                                                        }}
                                                                                        title={chc && chc.chcId ? "View CHC Details" : "CHC data not available"}
                                                                                        disabled={!chc || !chc.chcId}
                                                                                      >
                                                                                        <i><FontAwesomeIcon icon={faEye} /></i>
                                                                                      </button>
                                                                                    </td>
                                                            </tr>
                                                          ))
                                                        ) : (
                                                          <tr>
                                                            <td colSpan="8" className="text-center">No CHC data available</td>
                                                          </tr>
                                                        )}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </td>
                                              </tr>
                                            ) : selectedDistrictDataType === "Individual" ? (
                                              /* Individual (ExpensiveMachinery Beneficiaries) Table */
                                              <tr>
                                                <td colSpan="5" className="p-0">
                                                  <div className="table-responsive">
                                                    <table className="table table-bordered mb-0">
                                                      <thead>
                                                        <tr className="table-warning">
                                                          <th colSpan="6" className="fw-bold">Individuals under {surveyor.userName}</th>
                                                        </tr>
                                                        <tr className="table-secondary">
                                                          <th>Sr. No.</th>
                                                          <th>Farmer Name</th>
                                                          <th>Mobile</th>
                                                          <th>Village</th>
                                                          <th>State</th>
                                                          <th>Action</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {(expBenBySurveyor[surveyorKey] || []).length > 0 ? (
  (expBenBySurveyor[surveyorKey] || []).map((ben, bIdx) => (
    <tr key={`${surveyorKey}-ben-${bIdx}`}>
      <td>{bIdx + 1}</td>
      <td>{ben.farmerName}</td>
      <td>{ben.mobileNumber}</td>
      <td>{ben.villageName}</td>
      <td>{ben.stateName}</td>
      <td>
        <button
          className={`btn ${ben && ben.beneficiaryId ? 'btn-primary' : 'btn-secondary'}`}
          type="button"
          onClick={() => {
            console.log('Beneficiary data:', ben);

            if (!ben || !ben.beneficiaryId) {
              alert('Beneficiary data is not available or incomplete. Please try again.');
              console.error('❌ Invalid beneficiary data:', ben);
              return;
            }

            const surveyorUserCode = surveyor.userCode || surveyor.id;

            navigate("/MNT/ExpMachineryDetailView", {
              state: {
                beneficiaryId: ben.beneficiaryId,
                surveyorUserCode: parseInt(surveyorUserCode),
                farmerData: {
                farmerCode: ben.farmerCode || ben.beneficiaryId.toString(),
                farmerName: ben.farmerName || 'N/A',
                mobileNo: ben.mobileNumber || 'N/A',
                villageName: ben.villageName || 'N/A',
                stateName: ben.stateName || 'N/A'
                },
                returnTo: "/MNT/dashboardMnT",
                restoreState: true,
                expandedRows,
                expandedSurveyorRows,
                surveyorDetailsByDistrict,
                expBenBySurveyor,
                farmerDetailsBySurveyor: expBenBySurveyor
              }
            });
          }}
          title={ben && ben.beneficiaryId ? "View Beneficiary Details" : "Beneficiary data not available"}
          disabled={!ben || !ben.beneficiaryId}
        >
          <i><FontAwesomeIcon icon={faEye} /></i>
        </button>
      </td>
    </tr>
  ))
) : (
  <tr>
    <td colSpan="6" className="text-center">No beneficiary data available</td>
  </tr>
)}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </td>
                                              </tr>
                                            ) : (
                                              /* Farmer Table */
                                              <tr>
                                                <td colSpan="5" className="p-0">
                                                  <div className="table-responsive">
                                                    <table className="table table-bordered mb-0">
                                                      {/* Farmer Section Header */}
                                                      <thead>
                                                        <tr className="table-warning">
                                                          <th colSpan="8" className="fw-bold">
                                                            Farmers under {surveyor.userName}
                                                          </th>
                                                        </tr>
                                                        {/* Farmer Table Headers */}
                                                        <tr className="table-secondary">
                                                          <th>Sr. No.</th>
                                                          <th>Farmer Name</th>
                                                          <th>Mobile No</th>
                                                          <th>District</th>
                                                          <th>Village</th>
                                                          <th>Crop Name</th>
                                                          <th>Area (HA)</th>
                                                          <th>Action</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {/* Farmer Data Rows */}
                                                        {farmerData.length > 0 ? (
                                                          farmerData.map((farmer, farmerIndex) => (
                                                            <tr key={`${surveyorKey}-${farmerIndex}`}>
                                                              <td>{farmerIndex + 1}</td>
                                                              <td>{farmer.farmerName || 'N/A'}</td>
                                                              <td>{farmer.mobileNo || 'N/A'}</td>
                                                              <td>{farmer.districtName || 'N/A'}</td>
                                                              <td>{farmer.villageName || 'N/A'}</td>
                                                              <td>{farmer.cropName || 'N/A'}</td>
                                                              <td>{farmer.totalAreaInHA || 'N/A'}</td>
                                                              <td>
                                                                <button 
                                                                  className={`btn btn-sm ${farmer && (farmer.farmerCode || farmer.beneficiaryId || farmer.beneficiaryID || farmer.polygonShape || farmer.imagePath) ? 'btn-primary' : 'btn-secondary'}`}
                                                                  type="button" 
                                                                  onClick={() => {
                                                                    console.log('Farmer data:', farmer);
                                                                    
                                                                    // Validate farmer data before navigation
                                                                    if (!farmer) {
                                                                      alert('Farmer data is not available or incomplete. Please try again.');
                                                                      console.error('❌ Invalid farmer data:', farmer);
                                                                      return;
                                                                    }
                                                                    
                                                                    const farmerDetails = {
                                                                      farmerCode: farmer.farmerCode || farmer.beneficiaryId || farmer.beneficiaryID || farmer.id || farmer.Id || null,
                                                                      beneficiaryId: farmer.beneficiaryId || farmer.beneficiaryID || farmer.beneficiary_id || null,
                                                                      polygonShape: farmer.polygonShape || farmer.strPolygonShape || null,
                                                                      imagePath: farmer.imagePath || farmer.imagepath || farmer.imageName || farmer.imagename || null,
                                                                      farmerName: farmer.farmerName || 'N/A',
                                                                      stateName: farmer.stateName || 'N/A',
                                                                      districtName: farmer.districtName || 'N/A',
                                                                      villageName: farmer.villageName || 'N/A',
                                                                      totalAreaInHA: farmer.totalAreaInHA || 'N/A',
                                                                      mobileNo: farmer.mobileNo || 'N/A',
                                                                      aadharNo: farmer.aadharNo || 'N/A',
                                                                      cropName: farmer.cropName || 'N/A',
                                                                      seasonName: farmer.seasonName || 'N/A',
                                                                      fyName: farmer.fyName || 'N/A'
                                                                    };
                                                                    
                                                                    console.log('✅ Valid farmer details:', farmerDetails);
                                                                    
                                                                    navigate("/MNT/SurveyorDetailView", { 
                                                                      state: { 
                                                                        farmerData: farmerDetails,
                                                                        returnTo: "/MNT/dashboardMnT",
                                                                        restoreState: true,
                                                                        expandedRows,
                                                                        expandedSurveyorRows,
                                                                        surveyorDetailsByDistrict,
                                                                        farmerDetailsBySurveyor
                                                                      } 
                                                                    });
                                                                  }}
                                                                  title={farmer ? "View Farmer Details" : "Farmer data not available"}
                                                                  disabled={!farmer}
                                                                >
                                                                  <i><FontAwesomeIcon icon={faEye} /></i>
                                                                </button>
                                                              </td>
                                                            </tr>
                                                          ))
                                                        ) : (
                                                          <tr>
                                                            <td colSpan="8" className="text-center">No farmer data available</td>
                                                          </tr>
                                                        )}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </>
                                        )}
                                      </React.Fragment>
                                    );
                                  })
                                ) : (
                                  <tr className="table-light">
                                    <td colSpan="4" className="text-center ps-4">No surveyor data available</td>
                                  </tr>
                                )}
                              </>
                            )}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">No district data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Data Report Details Table */}
      {apiDataReportVisible && isCRMComponent && (
        <div className="col-lg-12" id="api-data-report-table">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">API Data Report Details</h5>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <label htmlFor="finYearSelect" className="me-2 mb-0">Financial Year:</label>
                  <select 
                    id="finYearSelect"
                    className="form-select form-select-sm" 
                    style={{ width: "200px" }}
                    value={selectedFinYear}
                    onChange={handleFinYearChange}
                  >
                    <option value="">All Years</option>
                    <option value="2020-21">2020-21</option>
                    <option value="2021-22">2021-22</option>
                    <option value="2022-23">2022-23</option>
                    <option value="2023-24">2023-24</option>
                    <option value="2024-25">2024-25</option>
                    <option value="2025-26">2025-26</option>
                  </select>
                </div>
                <button 
                  className="btn btn-sm btn-success" 
                  onClick={() => exportToCSV(apiDataReportDetails, "MT_API_Data_Report", ["S.No", "Sub District Name", "Record Count"], 'apiReport')}
                >
                  Export CSV
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr. No.</th>
                      <th>Sub District Name</th>
                      <th>Record Count</th>
                      <th>View Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiDataReportDetails && apiDataReportDetails.length > 0 ? (
                      apiDataReportDetails.map((report, index) => (
                        <React.Fragment key={report.blockCode || index}>
                          <tr>
                            <td>{index + 1}</td>
                            <td>{report.blockName || 'N/A'}</td>
                            <td>{report.recordCount || 0}</td>
                            <td>
                              <span 
                                role="button"
                                onClick={() => handleApiDataReportToggle(index, report)}
                                style={{
                                  cursor: "pointer",
                                  color: "#0d6efd",
                                  fontSize: "16px",
                                  fontWeight: "bold"
                                }}
                              >
                                {expandedApiDataReportIndex === index ? "-" : "+"}
                              </span>
                            </td>
                          </tr>
                          {expandedApiDataReportIndex === index && expandedApiDataReportDistrictData.length > 0 && (
                            <tr>
                              <td colSpan={4}>
                                <div className="ms-4 mt-2">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h5 className="mb-0">CHC Details for {report.blockName}</h5>
                                    <button 
                                      className="btn btn-sm btn-outline-success" 
                                      onClick={() => exportToCSV(
                                        expandedApiDataReportDistrictData,
                                        `MT_API_Data_Report_CHC_${report.blockName}`,
                                        ["S.No", "CHC Name", "Address", "Registration No.", "Mobile No.", "No of Implement"],
                                        'apiChc'
                                      )}
                                    >
                                      Export CSV
                                    </button>
                                  </div>
                                  <table className="table table-sm table-bordered mb-0">
                                    <thead>
                                      <tr>
                                        <th>Sr. No.</th>
                                        <th>CHC Name</th>
                                        <th>Address</th>
                                        <th>Registration No.</th>
                                        <th>Mobile No.</th>
                                        <th>Image</th>
                                        <th>No of Implement</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expandedApiDataReportDistrictData.map((chc, cIndex) => (
                                        <tr key={chc.id || cIndex}>
                                          <td>{cIndex + 1}</td>
                                          <td>{chc.chcName}</td>
                                          <td>{chc.landMarkAddress}</td>
                                          <td>{chc.societyRegistrationNumber}</td>
                                          <td>{chc.mobileNo}</td>
                                          <td>
                                            {chc.imagePath ? (
                                              <FontAwesomeIcon
                                                icon={faEye}
                                                onClick={() => handleImageView(chc.imagePath)}
                                                style={{
                                                  cursor: "pointer",
                                                  color: "#198754",
                                                  fontSize: "18px"
                                                }}
                                                title="View Image"
                                              />
                                            ) : (
                                              <span style={{ color: "#999" }}>N/A</span>
                                            )}
                                          </td>
                                          <td>
                                            {chc.noOfImplements > 0 ? (
                                              <span
                                                onClick={() => handleImplementsClick(chc)}
                                                style={{
                                                  cursor: "pointer",
                                                  color: "#0066cc",
                                                  textDecoration: "underline"
                                                }}
                                                title="Click to view implement details"
                                              >
                                                {chc.noOfImplements}
                                              </span>
                                            ) : (
                                              <span style={{ color: "#999" }}>0</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">
                          {localLoading ? 'Loading...' : 'No data available'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImageUrl && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
          aria-labelledby="imageModalLabel"
          aria-hidden="false"
          onClick={handleCloseImageModal}
        >
          <div 
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="imageModalLabel">
                  Individual Image
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseImageModal}
                  aria-label="Close"
                >x</button>
              </div>
              <div className="modal-body text-center">
                <img
                  src={selectedImageUrl}
                  alt="Individual Image"
                  className="img-fluid"
                  style={{ maxHeight: '70vh' }}
                  onError={(e) => {
                    console.error('Image failed to load:', e.target.src);
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<p class="text-danger">Failed to load image</p>';
                  }}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseImageModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Implement Details Modal */}
      {showImplementsModal && selectedImplements && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(193, 183, 183, 0)' }}
          tabIndex="-1"
          aria-labelledby="implementModalLabel"
          aria-hidden="false"
          onClick={handleCloseImplementsModal}
        >
          <div 
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#c9ebc9', borderBottom: '0px solid #dee2e6' }}>
                <h5 className="modal-title" id="implementModalLabel" style={{ fontWeight: 'bold' }}>
                  Implement Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseImplementsModal}
                  aria-label="Close"
                >x</button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <p>
                    <strong>Individual Name:</strong> {selectedImplements.displayName || 'N/A'};{' '}
                    <strong>Total Implements:</strong> {selectedImplements.noOfImplements ?? (selectedImplements.implementDetails ? selectedImplements.implementDetails.length : 'N/A')}
                  </p>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered table-striped">
                    <thead className="table-light123">
                      <tr>
                        <th style={{ width: '10%' }}>S.No</th>
                        <th style={{ width: '45%' }}>Category Name</th>
                        <th style={{ width: '45%' }}>Implement Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        try {
                          const implementDetails = selectedImplements.implementDetails
                            ? selectedImplements.implementDetails
                            : selectedImplements.implementDetailsJSON
                              ? JSON.parse(selectedImplements.implementDetailsJSON)
                              : [];

                          if (!Array.isArray(implementDetails) || implementDetails.length === 0) {
                            return (
                              <tr>
                                <td colSpan="3" className="text-center">No implement details available</td>
                              </tr>
                            );
                          }

                          return implementDetails.map((item, index) => {
                            const categoryName = item.categoryName || item.CategoryName || 'N/A';
                            const implementName = item.implementName || item.ImplementName || item.ImplementSubsidyName || 'N/A';

                            return (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{categoryName}</td>
                                <td>{implementName}</td>
                              </tr>
                            );
                          });
                        } catch (error) {
                          console.error('Error parsing implement details:', error);
                          return (
                            <tr>
                              <td colSpan="3" className="text-center text-danger">Error loading implement details</td>
                            </tr>
                          );
                        }
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer" style={{ backgroundColor: '#c9ebc9', borderTop: '1px solid #dee2e6' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseImplementsModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DistrictLoginDashboard;
